/**
 * ActionModal - large modal for creating / editing a single
 * `WorkflowAction` on the workflow's current version.
 *
 * Three sub-tabs: General / Condition & Overwrites / Effects.
 * Submit is atomic - every tab lives inside one generated
 * `useForm`; the form body is a single `type: 'custom'` field
 * whose value is the full `ActionFormValues` object (child tabs
 * read via `formCtx.values.form` and write via
 * `formCtx.setFieldValue('form.<key>', next)`).
 */

import { useCallback, useMemo, useState } from 'react'
import { toast } from 'sonner'

import { Button } from '@/shared/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/shared/components/ui/tabs'
import {
  useCreateAction,
  useUpdateWorkflowAction,
  type ActionCreateInput,
  type WorkflowActionAdmin,
  type WorkflowDefinitionAdmin,
  type WorkflowVersionAdmin,
} from '@/admin/hooks/use-workflow-definitions'
import { useForm } from '@/shared/lib/forms'
import type { FormField } from '@/shared/lib/forms'
import { toastApiError } from '@/shared/lib/api-error'

import { ConditionCaptureTab } from './condition-capture-tab'
import { EffectsGovernanceTab } from './effects-governance-tab'
import { GeneralTab } from './general-tab'
import { normalisePredicate } from './predicate-utils'
import { type ActionFormValues, type ActionFormContext } from './types'

type TabKey = 'general' | 'condition' | 'effects'

interface ActionModalProps {
  open: boolean
  onOpenChange: (next: boolean) => void
  mode: 'create' | 'edit'
  workflow: WorkflowDefinitionAdmin
  version: WorkflowVersionAdmin
  action?: WorkflowActionAdmin
}

export function ActionModal({
  open,
  onOpenChange,
  mode,
  workflow,
  version,
  action,
}: ActionModalProps) {
  const create = useCreateAction(workflow.uuid)
  const update = useUpdateWorkflowAction(workflow.uuid)

  const initialForm = useMemo<ActionFormValues>(
    () => buildInitialForm(action),
    [action],
  )

  // Tab selection is panel state owned by ModalBody, NOT hoisted here: the
  // ModalBody renders inside a generated-form `type: 'custom'` field, which
  // insulates it from this component's re-renders. Threading `tab` down as a
  // prop left the controlled <Tabs value> frozen at its initial value, so the
  // tabs appeared unclickable. Keeping the state local to ModalBody (the thing
  // that actually re-renders on click) fixes switching AND stops `tab` from
  // churning the `fields` identity, which was rebuilding the form on every tab
  // change.
  const fields = useMemo<FormField[]>(
    () => [
      {
        type: 'custom',
        name: 'form',
        className: 'col-span-2',
        value: initialForm,
        render: (formCtx) => (
          <ModalBody
            formCtx={formCtx as ActionFormContext}
            workflow={workflow}
            version={version}
            mode={mode}
          />
        ),
      },
    ],
    [initialForm, mode, workflow, version],
  )

  const { Form, ref } = useForm(
    {
      name: `workflow-action-${mode}`,
      submit: 'footer',
      fields,
    },
    async (rawValues) => {
      const form = (rawValues.form ?? {}) as ActionFormValues
      const payload = serialiseForm(form)
      try {
        if (mode === 'edit' && action) {
          await update.mutateAsync({ uuid: action.uuid, ...payload })
          toast.success('Action updated')
        } else {
          await create.mutateAsync(payload)
          toast.success('Action created')
        }
        onOpenChange(false)
      } catch (err) {
        toastApiError(err, `Failed to ${mode} action`)
      }
    },
    [open, mode, action?.uuid, fields],
  )

  const handleOpenChange = useCallback(
    (next: boolean) => {
      if (!next) {
        ref.current?.resetForm()
      }
      onOpenChange(next)
    },
    [onOpenChange, ref],
  )

  const busy = create.isPending || update.isPending

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="flex max-h-[85vh] flex-col overflow-hidden p-0 sm:max-w-[960px]">
        <DialogHeader className="px-6 pt-6">
          <DialogTitle>
            {mode === 'edit'
              ? `Edit Action - ${action?.label ?? ''}`
              : 'New Action'}
          </DialogTitle>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto px-6 py-2">
          <Form className="flex flex-col" />
        </div>
        <DialogFooter className="border-t-[0.5px] border-t-[var(--rule)] bg-[var(--bg-subtle)] px-6 py-3">
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={busy}
          >
            Cancel
          </Button>
          <Button
            onClick={() => ref.current?.submitForm()}
            disabled={busy}
            data-testid="workflows-action-submit"
          >
            {busy
              ? 'Saving…'
              : mode === 'edit'
                ? 'Save changes'
                : 'Create action'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

interface ModalBodyProps {
  formCtx: ActionFormContext
  workflow: WorkflowDefinitionAdmin
  version: WorkflowVersionAdmin
  mode: 'create' | 'edit'
}

function ModalBody({ formCtx, workflow, version, mode }: ModalBodyProps) {
  const [tab, setTab] = useState<TabKey>('general')
  return (
    <Tabs
      value={tab}
      onValueChange={(next) => setTab(next as TabKey)}
      className="w-full"
    >
      <TabsList variant="line">
        <TabsTrigger value="general">General</TabsTrigger>
        <TabsTrigger value="condition">Condition &amp; Overwrites</TabsTrigger>
        <TabsTrigger value="effects">Effects</TabsTrigger>
      </TabsList>

      <TabsContent value="general">
        <GeneralTab
          formCtx={formCtx}
          states={version.states}
          mode={mode}
          workflowUuid={workflow.uuid}
        />
      </TabsContent>

      <TabsContent value="condition">
        <ConditionCaptureTab
          formCtx={formCtx}
          workflowKey={workflow.key}
          resourceType={workflow.resource_type}
        />
      </TabsContent>

      <TabsContent value="effects">
        <EffectsGovernanceTab formCtx={formCtx} />
      </TabsContent>
    </Tabs>
  )
}

function buildInitialForm(
  action: WorkflowActionAdmin | undefined,
): ActionFormValues {
  return {
    key: action?.key ?? '',
    label: action?.label ?? '',
    icon: action?.icon ?? '',
    applicability: action?.applicability ?? 'STATE',
    actor: action?.actor ?? 'ADMIN',
    trigger: action?.trigger ?? 'USER',
    source_state: action?.source_state_uuid ?? null,
    target_state: action?.target_state_uuid ?? null,
    order: action?.order ?? 0,
    priority: action?.priority ?? 0,
    condition: normalisePredicate(action?.condition),
    side_effects: (action?.side_effects ?? []).map((s) => ({
      handler: s.handler,
      config: s.config ? structuredClone(s.config) : {},
      phase: s.phase ?? 'post_commit',
      failure_policy: s.failure_policy ?? 'fail',
      idempotent: s.idempotent ?? true,
      when: s.when ? structuredClone(s.when) : {},
    })),
    ui_hints: action?.ui_hints ? structuredClone(action.ui_hints) : {},
    required_attrs: Array.isArray(action?.required_attrs)
      ? [...action!.required_attrs]
      : [],
    overwrites: action?.overwrites ? structuredClone(action.overwrites) : {},
  }
}

function serialiseForm(form: ActionFormValues): ActionCreateInput {
  return {
    key: form.key.trim(),
    label: form.label.trim(),
    icon: form.icon?.trim() ?? '',
    applicability: form.applicability,
    actor: form.actor,
    trigger: form.trigger,
    source_state: form.source_state || null,
    target_state: form.target_state || null,
    order: Number(form.order) || 0,
    priority: Number(form.priority) || 0,
    condition: form.condition ?? {},
    side_effects: (form.side_effects ?? []).map((s) => ({
      handler: s.handler,
      config: s.config ?? {},
      phase: s.phase,
      failure_policy: s.failure_policy,
      idempotent: s.idempotent ?? true,
      when: s.when ?? {},
    })),
    ui_hints: form.ui_hints ?? {},
    required_attrs: form.required_attrs ?? [],
    overwrites: form.overwrites ?? {},
  }
}
